/* eslint-disable eqeqeq */
import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { DynamicGuardService } from '@workflow/dynamic-guard/dynamic-guard.decorator';
import { StatefulService } from '@app/service/stateful.service';

import { WorkflowService } from '@workflow/service/workflow/workflow.service';
import { Observable } from 'rxjs';
import { RouteHistoryService } from '@workflow/ecp-shared/service/route-history.service';
import { Title } from '@angular/platform-browser';

@DynamicGuardService('PreventAutofillRevisit')
@Injectable({ providedIn: 'root' })
export class PreventAutofillRevisitGuard {
  private readonly statefulService = inject(StatefulService);
  private readonly workflowService = inject(WorkflowService);
  private readonly routeHistoryService = inject(RouteHistoryService);
  private readonly titleService = inject(Title);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    return this.isNavigationAllowed(state.url, route);
  }

  public canDeactivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {
    // Use the unified path check for both activation and deactivation
    const allowed = this.isNavigationAllowed(state.url, route, true);
    if (!allowed) {
      // If navigation is not allowed, set the title to the current page name
      this.titleService.setTitle(this.workflowService.currentPage.customTitleOrDefault);
    }
    return allowed;
  }

  /**
   * Unified navigation check for both canActivate and canDeactivate.
   * @param url The URL to check
   * @param route The activated route snapshot
   * @param isDeactivate Whether this is a deactivation check (default: false)
   */
  private isNavigationAllowed(url: string, route: ActivatedRouteSnapshot = undefined, isDeactivate = false): boolean {
    const allowedAutofillType = ['Prefill', 'MobileNumber'];
    const flow = this.workflowService.currentFlow; // Ensure current flow is initialized
    let currentPage = this.workflowService.currentPage; // Default to current page
    let checkUrl = url;

    // For deactivation, try to get the next URL and page
    if (isDeactivate && this.routeHistoryService.getNextUrl) {
      const nextURL = this.routeHistoryService.getNextUrl();
      if (nextURL) {
        checkUrl = nextURL;
        // Remove fragment/hash and query string from url
        const cleanUrl = nextURL.split('#')[0].split('?')[0];
        // Extract page id from url (assuming last segment is the page id, adjust as needed)
        const segments = cleanUrl.split('/').filter(Boolean);
        const pageId = segments[segments.length - 1];
        if (pageId) {
          const foundPage = flow.pages.find((p) => p.path === pageId || p.config.id === pageId);
          if (foundPage) {
            currentPage = foundPage;
          }
        }
      }
    }

    // Always allow if not streamlined or not a skip page
    if (!flow.config.streamlined || !flow.config.streamlinedSkipPages.includes(currentPage.config.id)) {
      return true;
    }

    // Allow if fragment is present and page is not locked
    if (route.fragment && !currentPage.config.lockOnPrefill) {
      return true;
    }

    // Check forward navigation using RouteHistoryService
    if (this.routeHistoryService.isForwardNavigation(flow, checkUrl)) {
      return true;
    }

    // Allow if autofill type is not in allowed list
    const prefillPath = this.statefulService.wrap(flow.config.streamlinedPrefillPath);
    if (!allowedAutofillType.includes(prefillPath.value?.toString())) {
      return true;
    }

    // Allow if page is not complete
    if (!currentPage.helper.isPageComplete) {
      return true;
    }

    // Allow if current page is in failed pages list
    const failedPages = (this.statefulService.wrap('ss:pages-failed-on-autofill').value + '')?.split(',') || [];
    if (failedPages.includes(currentPage.config.id)) {
      return true;
    }
    // If none of the conditions are met, block navigation
    return false;
  }
}
